import { APIError } from '../types/api';

/**
 * Standardized API response handler
 * Extracts data from various response formats
 */
export function handleApiResponse<T>(response: any): T {
  // Check if response is an object
  if (typeof response !== 'object' || response === null) {
    throw new Error('Invalid API response format: expected object, got ' + typeof response);
  }

  // Handle response with success property
  if ('success' in response) {
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || response.message || 'Request failed');
    }
  }

  // Return response directly if it doesn't have success property
  return response;
}

/**
 * Standardized error handler
 * Formats errors for consistent handling
 */
export function handleApiError(error: any): {
  code: string;
  message: string;
  details?: any;
  userMessage: string;
} {
  // Check if it's an APIError
  if (error.code && error.message) {
    const apiError = error as APIError;
    return {
      code: apiError.code,
      message: apiError.message,
      details: apiError.details,
      userMessage: getUserMessage(apiError.code, apiError.message)
    };
  }
  
  // Handle generic errors
  return {
    code: 'UNKNOWN',
    message: error.message || 'Unknown error',
    details: undefined,
    userMessage: 'エラーが発生しました。もう一度お試しください。'
  };
}

/**
 * Get user-friendly error message based on error code
 */
function getUserMessage(code: string, defaultMessage: string): string {
  const errorMessages: Record<string, string> = {
    '400': '入力内容に誤りがあります。',
    '401': '認証エラーが発生しました。',
    '403': 'アクセス権限がありません。',
    '404': 'リソースが見つかりませんでした。',
    '413': 'ファイルサイズが大きすぎます。',
    '422': '入力データが正しくありません。',
    '429': 'リクエストが多すぎます。しばらくしてから再度お試しください。',
    '500': 'サーバーエラーが発生しました。',
    '502': 'サーバーとの接続に問題が発生しました。',
    '503': 'サービスが一時的に利用できません。',
    '504': 'リクエストがタイムアウトしました。'
  };
  
  return errorMessages[code] || defaultMessage;
}

/**
 * Options for createApiRequest
 */
export interface ApiRequestOptions<T> {
  request: () => Promise<any>;
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
  onFinally?: () => void;
  transformer?: (data: any) => T;
}

/**
 * Create standardized API request with consistent error handling
 */
export async function createApiRequest<T>(options: ApiRequestOptions<T>): Promise<T> {
  const { request, onSuccess, onError, onFinally, transformer } = options;
  
  try {
    const response = await request();
    let data = handleApiResponse<T>(response);
    
    // Apply transformer if provided
    if (transformer) {
      data = transformer(data);
    }
    
    // Call success callback
    if (onSuccess) {
      onSuccess(data);
    }
    
    return data;
  } catch (error) {
    const formattedError = handleApiError(error);
    
    // Call error callback
    if (onError) {
      onError(formattedError);
    }
    
    throw error;
  } finally {
    // Call finally callback
    if (onFinally) {
      onFinally();
    }
  }
}